import { useEffect, useRef, useState } from 'react'
import { juntar } from './ui'

/**
 * O vídeo da conversa com controles do próprio CRM.
 *
 * Os controles do navegador tinham um botão de tela cheia que, na janela em
 * que o CRM roda, não funciona: o clique é engolido sem aviso nenhum — nem
 * tela cheia, nem erro. Sem aviso, não há o que fazer em volta dele. Com o
 * botão sendo nosso, o clique é nosso também: pede tela cheia ao navegador e,
 * se ela não vier, abre o vídeo ocupando a janela inteira.
 *
 * O desenho imita o do navegador — play, tempo, barra, volume e tela cheia
 * no canto de baixo à direita —, porque é o lugar em que a mão já vai.
 */

/**
 * Quanto esperar a tela cheia antes de concluir que o navegador não vai dar.
 *
 * Curto de propósito: é espera que o usuário sente com o dedo no botão. Não
 * precisa cobrir a animação de entrada em tela cheia, porque o pedido avisa
 * quando é aceito — e esse aviso cancela a espera.
 */
const ESPERA_DA_TELA_CHEIA_MS = 150

/**
 * Este navegador já recusou tela cheia uma vez.
 *
 * Recusar é característica da janela, não do vídeo: uma vez descoberto, os
 * cliques seguintes abrem o visualizador na hora, sem repetir a espera. Vale
 * enquanto a página estiver aberta.
 */
let telaCheiaBloqueada = false

function tempo(segundos: number): string {
  if (!Number.isFinite(segundos) || segundos < 0) return '0:00'
  const total = Math.floor(segundos)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export function PlayerDeVideo({
  url,
  aoTelaCheiaRecusada,
  tocarAoAbrir = false,
  semTelaCheia = false,
  className,
}: {
  url: string
  /** Chamado quando o navegador não entra em tela cheia. */
  aoTelaCheiaRecusada?: () => void
  tocarAoAbrir?: boolean
  /** Dentro do visualizador, que já ocupa a janela: não há para onde ampliar. */
  semTelaCheia?: boolean
  className?: string
}) {
  const caixa = useRef<HTMLDivElement>(null)
  const video = useRef<HTMLVideoElement>(null)
  const [tocando, setTocando] = useState(false)
  const [atual, setAtual] = useState(0)
  const [duracao, setDuracao] = useState(0)
  const [mudo, setMudo] = useState(false)
  const [volume, setVolume] = useState(1)
  const [emTelaCheia, setEmTelaCheia] = useState(false)

  useEffect(() => {
    const aoMudar = () => setEmTelaCheia(document.fullscreenElement === caixa.current)
    document.addEventListener('fullscreenchange', aoMudar)
    return () => document.removeEventListener('fullscreenchange', aoMudar)
  }, [])

  function alternarPlay() {
    const v = video.current
    if (!v) return
    if (v.paused) void v.play()
    else v.pause()
  }

  /*
    Tela cheia com rede de segurança.

    O pedido pode dar certo, falhar com erro, ou — nesta janela — simplesmente
    nunca responder. Os três viram uma pergunta só: passado o tempo de espera,
    estamos em tela cheia? Se não, o vídeo abre no visualizador.
  */
  function alternarTelaCheia() {
    const elemento = caixa.current
    if (!elemento) return

    if (document.fullscreenElement) {
      void document.exitFullscreen()
      return
    }

    let resolvido = false
    const recusar = () => {
      if (resolvido) return
      resolvido = true
      telaCheiaBloqueada = true
      video.current?.pause()
      aoTelaCheiaRecusada?.()
    }

    // Já se sabe que esta janela não dá tela cheia: abre direto, sem espera.
    if (telaCheiaBloqueada) {
      recusar()
      return
    }

    const aceitar = () => {
      resolvido = true
      window.clearTimeout(relogio)
      document.removeEventListener('fullscreenchange', aoEntrar)
    }
    // O evento chega antes de a animação terminar, e é ele que cancela a espera.
    const aoEntrar = () => {
      if (document.fullscreenElement === elemento) aceitar()
    }
    document.addEventListener('fullscreenchange', aoEntrar)

    const relogio = window.setTimeout(() => {
      document.removeEventListener('fullscreenchange', aoEntrar)
      if (document.fullscreenElement !== elemento) recusar()
      else resolvido = true
    }, ESPERA_DA_TELA_CHEIA_MS)

    try {
      const pedido = elemento.requestFullscreen?.()
      if (!pedido) {
        window.clearTimeout(relogio)
        document.removeEventListener('fullscreenchange', aoEntrar)
        recusar()
        return
      }
      pedido.then(aceitar, () => {
        window.clearTimeout(relogio)
        document.removeEventListener('fullscreenchange', aoEntrar)
        recusar()
      })
    } catch {
      window.clearTimeout(relogio)
      document.removeEventListener('fullscreenchange', aoEntrar)
      recusar()
    }
  }

  const progresso = duracao > 0 ? (atual / duracao) * 100 : 0

  return (
    <div
      ref={caixa}
      className={juntar(
        'group relative overflow-hidden bg-black',
        emTelaCheia ? 'flex h-full w-full items-center justify-center' : 'rounded',
        className,
      )}
    >
      <video
        ref={video}
        src={url}
        preload="metadata"
        playsInline
        autoPlay={tocarAoAbrir}
        onClick={(e) => {
          // Dentro do visualizador, o clique no fundo fecha. O vídeo não é
          // fundo: aqui ele só dá play e pausa.
          e.stopPropagation()
          alternarPlay()
        }}
        onPlay={() => setTocando(true)}
        onPause={() => setTocando(false)}
        onEnded={() => setTocando(false)}
        onTimeUpdate={(e) => setAtual(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuracao(e.currentTarget.duration)}
        onDurationChange={(e) => setDuracao(e.currentTarget.duration)}
        onVolumeChange={(e) => {
          setMudo(e.currentTarget.muted)
          setVolume(e.currentTarget.volume)
        }}
        className="h-full w-full cursor-pointer object-contain"
      />

      {/*
        A barra fica à vista enquanto o vídeo está parado e some tocando, e
        volta ao passar o mouse — o mesmo comportamento do navegador, para
        ninguém estranhar.
      */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={juntar(
          'absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-6 text-white transition-opacity',
          tocando ? 'opacity-0 group-hover:opacity-100' : 'opacity-100',
        )}
      >
        <input
          type="range"
          min={0}
          max={duracao || 0}
          step={0.1}
          value={atual}
          onChange={(e) => {
            const v = video.current
            if (v) v.currentTime = Number(e.target.value)
          }}
          aria-label="Posição do vídeo"
          className="regua-do-player mb-1 block h-3 w-full"
          style={{ '--preenchido': `${progresso}%` } as React.CSSProperties}
        />

        <div className="flex items-center gap-1">
          <BotaoDoPlayer rotulo={tocando ? 'Pausar' : 'Tocar'} onClick={alternarPlay}>
            {tocando ? (
              <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor" />
            ) : (
              <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
            )}
          </BotaoDoPlayer>

          <span className="px-1 text-xs tabular-nums">
            {tempo(atual)} / {tempo(duracao)}
          </span>

          <span className="flex-1" />

          {/*
            Volume: o botão continua tirando e devolvendo o som num clique, e a
            régua abre para a direita dele ao encostar.

            Ela cresce dentro da própria barra, empurrando de leve o botão de
            tela cheia, em vez de subir por cima do vídeo. A caixa é a mesma do
            botão: com a régua do lado de fora, arrastar o cursor tirava o
            ponteiro do botão e ela sumia no meio do ajuste.
          */}
          {/*
            A pílula escura aparece junto com a régua.

            Aberta, o alto-falante e a régua viram uma peça só sobre a imagem
            do vídeo — que pode ser clara e engolir uma linha branca solta.
            Fechada, o botão volta a ser igual aos outros da barra.
          */}
          <div className="group/volume flex items-center rounded-full transition-colors hover:bg-black/55">
            <BotaoDoPlayer
              rotulo={mudo || volume === 0 ? 'Ativar som' : 'Tirar som'}
              onClick={() => {
                const v = video.current
                if (!v) return
                // Sem som e no zero: devolver o som precisa de um volume para voltar.
                if ((v.muted || v.volume === 0) && v.volume === 0) v.volume = 1
                v.muted = !v.muted
              }}
            >
              <path d="M4 9v6h4l5 4V5L8 9z" fill="currentColor" />
              {mudo || volume === 0 ? (
                <path d="M16 9l5 6M21 9l-5 6" />
              ) : (
                <path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" />
              )}
            </BotaoDoPlayer>

            <div className="w-0 overflow-hidden opacity-0 transition-all duration-150 group-hover/volume:w-16 group-hover/volume:opacity-100 group-focus-within/volume:w-16 group-focus-within/volume:opacity-100">
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={mudo ? 0 : volume}
                onChange={(e) => {
                  const v = video.current
                  if (!v) return
                  const novo = Number(e.target.value)
                  v.volume = novo
                  // Mexer na régua devolve o som: quem arrasta quer ouvir.
                  v.muted = novo === 0
                }}
                aria-label="Volume"
                className="regua-do-player mr-2 block h-3 w-12"
                style={{ '--preenchido': `${(mudo ? 0 : volume) * 100}%` } as React.CSSProperties}
              />
            </div>
          </div>

          {semTelaCheia ? null : (
            <BotaoDoPlayer
              rotulo={emTelaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
              onClick={alternarTelaCheia}
            >
              {emTelaCheia ? (
                <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
              ) : (
                <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
              )}
            </BotaoDoPlayer>
          )}
        </div>
      </div>
    </div>
  )
}

function BotaoDoPlayer({
  rotulo,
  onClick,
  children,
}: {
  rotulo: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={rotulo}
      title={rotulo}
      className="flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-white/15"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {children}
      </svg>
    </button>
  )
}
